import React, { forwardRef, useMemo } from 'react';
import { FunctionComponent } from 'react';
import ReactSelect from 'react-select';
import { useFallbackId } from '../../useFallbackId';
import classNames from 'classnames';
import styles from './styles.module.scss';

export type Option = {
    value: string;
    label: string;
};

type ReactSelectProps = React.ComponentProps<typeof ReactSelect<Option>>;

export type SelectProps = {
    id?: string;
    options: Array<Option>;
    value: string | null;
    onChange: (newValue: Option | null) => void;
    className?: string;
    disabled?: boolean;
} & Pick<
    ReactSelectProps,
    | 'onBlur'
    | 'onChange'
    | 'isClearable'
    | 'aria-errormessage'
    | 'aria-invalid'
    | 'aria-label'
    | 'aria-labelledby'
    | 'aria-live'
    | 'ariaLiveMessages'
    | 'ref'
    | 'isDisabled'
>;

export const Select: FunctionComponent<SelectProps> = forwardRef(
    ({ id, options, value, onChange, className, disabled, ...props }, ref) => {
        const inputId = useFallbackId(id);

        const currentValue = useMemo(() => {
            return options.find((option) => option.value === value) || null;
        }, [options, value]);

        return (
            <div>
                <ReactSelect<Option>
                    ref={ref}
                    id={inputId}
                    className={classNames(styles.select, className)}
                    options={options}
                    value={currentValue}
                    onChange={onChange}
                    isDisabled={disabled}
                    classNames={{
                        control: (state) =>
                            state.isFocused ? styles.control_focused : styles.control,
                    }}
                    theme={(theme) => ({
                        ...theme,
                        colors: {
                            ...theme.colors,
                            primary: '#579ad9',
                        },
                    })}
                    {...props}
                />
            </div>
        );
    }
);
